import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import SEO from '../components/SEO';
import { 
  CurrencyDollarIcon, 
  BuildingStorefrontIcon,
  MapPinIcon,
  CreditCardIcon,
  StarIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  ChartBarIcon,
  HomeIcon,
  UserGroupIcon,
  BanknotesIcon
} from '@heroicons/react/24/outline';

// Custom hook for scroll animations
const useScrollAnimation = (options = {}) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
      }
    }, { threshold: 0.1, ...options });

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, []);

  return [ref, isVisible];
};

// Animated Section Component
const AnimatedSection = ({ children, className = '', delay = 0 }) => {
  const [ref, isVisible] = useScrollAnimation();
  
  return (
    <div
      ref={ref}
      className={`transition-all duration-1000 ${className}`}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(60px)',
        transitionDelay: `${delay}ms`
      }}
    >
      {children}
    </div>
  );
};

// Parallax Background Component
const ParallaxBg = () => {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Gradient Orbs */}
      <div 
        className="absolute w-[800px] h-[800px] rounded-full bg-purple-600/20 blur-3xl"
        style={{ 
          top: -200 + scrollY * 0.1, 
          left: -200,
          transform: `translateY(${scrollY * 0.1}px)`
        }}
      />
      <div 
        className="absolute w-[600px] h-[600px] rounded-full bg-blue-600/20 blur-3xl"
        style={{ 
          top: 200, 
          right: -100,
          transform: `translateY(${scrollY * 0.15}px)`
        }}
      />
      <div 
        className="absolute w-[400px] h-[400px] rounded-full bg-primary-600/30 blur-3xl"
        style={{ 
          bottom: -100, 
          left: '40%',
          transform: `translateY(${-scrollY * 0.08}px)`
        }}
      />
    </div>
  );
};

// Floating Card Component
const FloatingCard = ({ children, delay = 0, className = '' }) => {
  return (
    <div 
      className={`animate-float ${className}`}
      style={{ animationDelay: `${delay}s` }}
    >
      {children}
    </div>
  );
};

// Counter Animation Component
const AnimatedCounter = ({ end, duration = 2000, prefix = '', suffix = '' }) => {
  const [count, setCount] = useState(0);
  const [ref, isVisible] = useScrollAnimation();
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (isVisible && !hasAnimated.current) {
      hasAnimated.current = true;
      let start = 0;
      const increment = end / (duration / 16);
      const timer = setInterval(() => {
        start += increment;
        if (start >= end) {
          setCount(end);
          clearInterval(timer);
        } else {
          setCount(Math.floor(start));
        }
      }, 16);
      return () => clearInterval(timer);
    }
  }, [isVisible, end, duration]);

  return <span ref={ref}>{prefix}{count.toLocaleString()}{suffix}</span>;
};

const Home = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [userType, setUserType] = useState(() => {
    return localStorage.getItem('locuUserType') || 'members';
  });
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleUserTypeChange = () => {
      const savedUserType = localStorage.getItem('locuUserType') || 'members';
      setUserType(savedUserType);

      // Clear merchant intent flags when viewing members section
      if (savedUserType === 'members') {
        localStorage.removeItem('merchantIntent');
        sessionStorage.removeItem('isMerchant');
      }
    };

    // Run on mount to clear flags if needed
    handleUserTypeChange();

    window.addEventListener('userTypeChanged', handleUserTypeChange);
    return () => window.removeEventListener('userTypeChanged', handleUserTypeChange);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const handleGetStarted = () => {
    navigate('/login');
  };

  // ==================== MERCHANT VIEW ====================
  if (userType === 'merchants') {
    return (
      <div className="min-h-screen bg-[#0a0a0f] text-white overflow-hidden">
        <SEO
          title="LOCU — Earn Rewards on Rent, Save at Restaurants"
          description="Pay rent, earn 2% back as LOCU points. Redeem for up to 10% off at 50+ partner restaurants near you. Join community dining events with verified neighbors. Free to join."
          path="/"
          keywords="rent rewards, earn points on rent, restaurant discounts, neighborhood dining, LOCU loyalty program"
          structuredData={{
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How do I earn rewards on rent with LOCU?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Pay your rent through LOCU and earn 2% back as LOCU points on every payment. That's up to ₹7,200/year on a ₹30,000 monthly rent. No fees, instant points."
                }
              },
              {
                "@type": "Question",
                "name": "Where can I redeem LOCU points?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Redeem your LOCU points at 50+ partner restaurants, cafes, and bars in your neighbourhood. Get up to 10% off on your dining bills."
                }
              },
              {
                "@type": "Question",
                "name": "What are LOCU community dining events?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "LOCU Connect lets you join community dining events at local restaurants. Meet verified neighbours over shared meals in groups of 4-8. No networking, just good food and genuine conversations."
                }
              }
            ]
          }}
        />
        <style>{`
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
          }
          @keyframes pulse-glow {
            0%, 100% { box-shadow: 0 0 20px rgba(124, 58, 237, 0.3); }
            50% { box-shadow: 0 0 40px rgba(124, 58, 237, 0.6); }
          }
          @keyframes gradient-shift {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          @keyframes text-shimmer {
            0% { background-position: -200% center; }
            100% { background-position: 200% center; }
          }
          .animate-float { animation: float 6s ease-in-out infinite; }
          .animate-pulse-glow { animation: pulse-glow 3s ease-in-out infinite; }
          .gradient-text {
            background: linear-gradient(90deg, #7c3aed, #3b82f6, #7c3aed);
            background-size: 200% auto;
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            animation: text-shimmer 3s linear infinite;
          }
          .glass-card {
            background: rgba(255, 255, 255, 0.05);
            backdrop-filter: blur(10px);
            border: 1px solid rgba(255, 255, 255, 0.1);
          }
        `}</style>

        <ParallaxBg />

        {/* Hero Section */}
        <section className="relative min-h-screen flex items-center justify-center px-4">
          <div className="relative z-10 text-center max-w-5xl mx-auto">
            <AnimatedSection>
              <span className="inline-block px-4 py-2 rounded-full glass-card text-sm font-medium text-purple-300 mb-8">
                🚀 For Restaurant Partners
              </span>
            </AnimatedSection>
            
            <AnimatedSection delay={200}>
              <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold mb-8 leading-tight">
                Grow your business
                <br />
                <span className="gradient-text">with LOCU</span>
              </h1>
            </AnimatedSection>

            <AnimatedSection delay={400}>
              <p className="text-xl md:text-2xl text-gray-400 mb-12 max-w-3xl mx-auto">
                Get <span className="text-white font-semibold">featured in your neighborhood</span> and reach thousands of local renters.
                Earn extra revenue from <span className="text-white font-semibold">group dining events</span> through our Connect feature.
              </p>
            </AnimatedSection>

            <AnimatedSection delay={600}>
              {!isAuthenticated ? (
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={() => navigate('/login', { state: { isMerchant: true } })}
                    className="group relative px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl font-semibold text-lg overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-purple-500/25"
                  >
                    <span className="relative z-10">Get Started</span>
                    <div className="absolute inset-0 bg-gradient-to-r from-primary-400 to-primary-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                  <button
                    onClick={() => navigate('/merchant-contact')}
                    className="px-8 py-4 glass-card rounded-xl font-semibold text-lg hover:bg-white/10 transition-all duration-300"
                  >
                    Contact Sales
                  </button>
                </div>
              ) : user?.role === 'merchant' ? (
                <button
                  onClick={() => navigate('/merchant/dashboard')}
                  className="px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl font-semibold text-lg hover:scale-105 transition-transform"
                >
                  Go to Dashboard →
                </button>
              ) : null}
            </AnimatedSection>

            {/* Floating Stats Cards */}
            <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-6">
              <FloatingCard delay={0}>
                <AnimatedSection delay={800}>
                  <div className="glass-card rounded-2xl p-6 text-left hover:scale-105 transition-transform duration-300 animate-pulse-glow">
                    <MapPinIcon className="h-10 w-10 text-purple-400 mb-4" />
                    <h3 className="text-3xl font-bold mb-2">Top Spot</h3>
                    <p className="text-gray-400">Featured in your 5km neighborhood</p>
                  </div>
                </AnimatedSection>
              </FloatingCard>

              <FloatingCard delay={0.5}>
                <AnimatedSection delay={900}>
                  <div className="glass-card rounded-2xl p-6 text-left hover:scale-105 transition-transform duration-300 animate-pulse-glow">
                    <UserGroupIcon className="h-10 w-10 text-blue-400 mb-4" />
                    <h3 className="text-3xl font-bold mb-2">Extra Revenue</h3>
                    <p className="text-gray-400">From group dining Connect events</p>
                  </div>
                </AnimatedSection>
              </FloatingCard>

              <FloatingCard delay={1}>
                <AnimatedSection delay={1000}>
                  <div className="glass-card rounded-2xl p-6 text-left hover:scale-105 transition-transform duration-300">
                    <ChartBarIcon className="h-10 w-10 text-primary-400 mb-4" />
                    <h3 className="text-3xl font-bold mb-2">Growing</h3>
                    <p className="text-gray-400">Local renter community</p>
                  </div>
                </AnimatedSection>
              </FloatingCard>
            </div>
          </div>

          {/* Scroll Indicator */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
            <div className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-2">
              <div className="w-1 h-2 bg-white/50 rounded-full animate-pulse" />
            </div>
          </div>
        </section>

        {/* Value Props Section - Combined */}
        <section className="relative py-24 px-4">
          <div className="max-w-6xl mx-auto">
            <AnimatedSection>
              <h2 className="text-4xl md:text-5xl font-bold text-center mb-16">
                Two ways to <span className="gradient-text">grow revenue</span>
              </h2>
            </AnimatedSection>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Neighborhood Visibility */}
              <AnimatedSection>
                <div className="glass-card rounded-2xl p-8 h-full">
                  <MapPinIcon className="h-12 w-12 text-blue-400 mb-6" />
                  <h3 className="text-2xl font-bold mb-4">Featured in Your Neighborhood</h3>
                  <p className="text-gray-400 mb-6">
                    Top placement when local renters search for restaurants. Be the first they see in your 5km radius.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircleIcon className="h-5 w-5 text-blue-400 flex-shrink-0" />
                      <span className="text-gray-300">Growing local community</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircleIcon className="h-5 w-5 text-blue-400 flex-shrink-0" />
                      <span className="text-gray-300">Prime top-3 placement</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircleIcon className="h-5 w-5 text-blue-400 flex-shrink-0" />
                      <span className="text-gray-300">Hyperlocal targeting</span>
                    </div>
                  </div>
                </div>
              </AnimatedSection>

              {/* Connect Revenue */}
              <AnimatedSection delay={200}>
                <div className="glass-card rounded-2xl p-8 h-full">
                  <UserGroupIcon className="h-12 w-12 text-purple-400 mb-6" />
                  <h3 className="text-2xl font-bold mb-4">Connect Group Events</h3>
                  <p className="text-gray-400 mb-6">
                    Pre-booked group dining events with verified renters. Extra revenue with zero marketing effort.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircleIcon className="h-5 w-5 text-purple-400 flex-shrink-0" />
                      <span className="text-gray-300">Pre-booked groups</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircleIcon className="h-5 w-5 text-purple-400 flex-shrink-0" />
                      <span className="text-gray-300">Higher order values</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircleIcon className="h-5 w-5 text-purple-400 flex-shrink-0" />
                      <span className="text-gray-300">Fill slow hours automatically</span>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            </div>
          </div>
        </section>

        {/* Quick Benefits */}
        <section className="relative py-20 px-4 bg-gradient-to-b from-transparent to-purple-900/10">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-4 gap-6">
              {[
                { icon: ChartBarIcon, title: 'Analytics', desc: 'Real-time dashboard', color: 'green' },
                { icon: CreditCardIcon, title: 'Easy Payouts', desc: 'Automated settlements', color: 'orange' },
                { icon: StarIcon, title: 'Zero Marketing', desc: 'We bring customers', color: 'pink' },
                { icon: CheckCircleIcon, title: 'Support', desc: 'Dedicated team', color: 'cyan' },
              ].map((feature, idx) => (
                <AnimatedSection key={idx} delay={idx * 100}>
                  <div className="glass-card rounded-xl p-6 text-center hover:scale-105 transition-all duration-300">
                    <feature.icon className={`h-10 w-10 text-${feature.color}-400 mb-3 mx-auto`} />
                    <h3 className="text-lg font-bold mb-2">{feature.title}</h3>
                    <p className="text-sm text-gray-400">{feature.desc}</p>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        {!isAuthenticated && (
          <section className="relative py-24 px-4">
            <AnimatedSection>
              <div className="max-w-3xl mx-auto text-center">
                <h2 className="text-4xl md:text-5xl font-bold mb-6">
                  Ready to get started?
                </h2>
                <p className="text-lg text-gray-400 mb-8">
                  Join restaurants growing with LOCU
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={() => navigate('/merchant-contact')}
                    className="px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl font-semibold text-lg hover:scale-105 transition-transform"
                  >
                    Get Started Free
                  </button>
                  <button
                    onClick={() => navigate('/login', { state: { isMerchant: true } })}
                    className="px-8 py-4 glass-card rounded-xl font-semibold text-lg hover:bg-white/10 transition-all"
                  >
                    Sign In
                  </button>
                </div>
              </div>
            </AnimatedSection>
          </section>
        )}
      </div>
    );
  }

  // ==================== MEMBER VIEW ====================
  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white overflow-hidden">
      <SEO
        title="LOCU — Earn Rewards on Rent, Save at Restaurants"
        description="Pay rent, earn 2% back as LOCU points. Redeem for up to 10% off at 50+ partner restaurants near you. Join community dining events with verified neighbors. Free to join."
        path="/"
        keywords="rent rewards, earn points on rent, restaurant discounts, neighborhood dining, LOCU loyalty program"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How do I earn rewards on rent with LOCU?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Pay your rent through LOCU and earn 2% back as LOCU points on every payment. That's up to ₹7,200/year on a ₹30,000 monthly rent. No fees, instant points."
              }
            },
            {
              "@type": "Question",
              "name": "Where can I redeem LOCU points?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Redeem your LOCU points at 50+ partner restaurants, cafes, and bars in your neighbourhood. Get up to 10% off on your dining bills."
              }
            },
            {
              "@type": "Question",
              "name": "What are LOCU community dining events?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "LOCU Connect lets you join community dining events at local restaurants. Meet verified neighbours over shared meals in groups of 4-8. No networking, just good food and genuine conversations."
              }
            }
          ]
        }}
      />
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(2deg); }
        }
        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 0 20px rgba(124, 58, 237, 0.3); }
          50% { box-shadow: 0 0 60px rgba(124, 58, 237, 0.6); }
        }
        @keyframes text-shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes border-dance {
          0%, 100% { border-color: rgba(124, 58, 237, 0.5); }
          50% { border-color: rgba(59, 130, 246, 0.5); }
        }
        .animate-float { animation: float 6s ease-in-out infinite; }
        .animate-pulse-glow { animation: pulse-glow 3s ease-in-out infinite; }
        .animate-border { animation: border-dance 3s ease-in-out infinite; }
        .gradient-text {
          background: linear-gradient(90deg, #f97316, #ec4899, #8b5cf6, #f97316);
          background-size: 300% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: text-shimmer 4s linear infinite;
        }
        .glass-card {
          background: rgba(255, 255, 255, 0.03);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.08);
        }
        .glow-card {
          position: relative;
        }
        .glow-card::before {
          content: '';
          position: absolute;
          inset: -2px;
          background: linear-gradient(45deg, #7c3aed, #3b82f6, #7c3aed);
          border-radius: inherit;
          opacity: 0;
          transition: opacity 0.3s;
          z-index: -1;
          filter: blur(8px);
        }
        .glow-card:hover::before {
          opacity: 0.5;
        }
      `}</style>

      <ParallaxBg />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center px-4">
        <div className="relative z-10 text-center max-w-5xl mx-auto">
          <AnimatedSection>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card text-sm font-medium text-purple-300 mb-8">
              <StarIcon className="h-4 w-4" />
              <span>Rent rewards reimagined</span>
            </div>
          </AnimatedSection>
          
          <AnimatedSection delay={200}>
            <h1 className="text-5xl md:text-7xl lg:text-[6rem] font-bold mb-8 leading-[1.05] tracking-tight uppercase">
              UNLIKE YOUR EX,
              <br />
              <span className="gradient-text">WE REWARD LOYALTY</span>
            </h1>
          </AnimatedSection>

          <AnimatedSection delay={400}>
            <p className="text-xl md:text-2xl text-gray-400 mb-12 max-w-2xl mx-auto leading-relaxed">
              Turn your rent into rewards. Earn points every month, unlock discounts at local restaurants, and save while you pay.
            </p>
          </AnimatedSection>

          <AnimatedSection delay={600} className="relative z-30">
            {!isAuthenticated && (
              <>
                <p className="text-2xl md:text-3xl font-bold text-white mb-6">
                  Get 1000 LOCU Points <span className="text-orange-300">(worth ₹1000)</span> when you sign up!
                </p>
                <Link
                  to="/login"
                  className="group relative inline-flex items-center justify-center px-10 py-5 bg-gradient-to-r from-primary-500 via-primary-600 to-primary-700 rounded-2xl font-bold text-xl overflow-hidden transition-all duration-500 hover:scale-105 hover:shadow-2xl hover:shadow-primary-500/25"
                >
                  <span className="relative z-10 flex items-center gap-2">
                    Start earning today
                    <ArrowRightIcon className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              </>
            )}
          </AnimatedSection>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-white/20 flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-white/40 rounded-full" />
          </div>
        </div>
      </section>

      {/* Rent Calculator */}
      {!isAuthenticated && (
        <section className="relative py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <AnimatedSection>
              <div className="glass-card rounded-3xl p-8 md:p-12 text-center border border-white/10">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Calculate your rewards
                </h2>
                <p className="text-gray-400 mb-8">
                  See how much you could earn with LOCU
                </p>
                
                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  <div className="bg-white/5 rounded-2xl p-6">
                    <div className="text-sm text-gray-400 mb-2">Monthly Rent</div>
                    <div className="text-4xl font-bold text-white">₹30,000</div>
                  </div>
                  <div className="bg-white/5 rounded-2xl p-6">
                    <div className="text-sm text-gray-400 mb-2">Points/Month</div>
                    <div className="text-4xl font-bold text-primary-400">600</div>
                  </div>
                  <div className="bg-white/5 rounded-2xl p-6">
                    <div className="text-sm text-gray-400 mb-2">Yearly Value</div>
                    <div className="text-4xl font-bold text-green-400">₹7,200</div>
                  </div>
                </div>
                
                <p className="text-sm text-gray-500">
                  Based on average Bangalore rent. 2% back on every payment.
                </p>
              </div>
            </AnimatedSection>
          </div>
        </section>
      )}

      {/* Main Features Section - 4 Icons Before Sign In/Sign Up */}
      {!isAuthenticated && (
        <section className="relative py-20 px-4 -mt-10 z-40">
          <div className="max-w-6xl mx-auto">
            <AnimatedSection>
              <h2 className="text-3xl md:text-5xl font-bold text-center mb-4">
                Everything you need in <span className="gradient-text">one place</span>
              </h2>
              <p className="text-lg text-gray-400 text-center mb-12 max-w-2xl mx-auto">
                Discover how LOCU helps you earn rewards, pay rent, explore neighborhoods, and connect with others
              </p>
            </AnimatedSection>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {/* Home / Dashboard */}
              <AnimatedSection delay={0}>
                <div className="glass-card glow-card rounded-2xl p-8 hover:scale-105 transition-all duration-300 relative overflow-hidden border border-white/10 text-center">
                  <div className="absolute inset-0 bg-gradient-to-br from-primary-400 to-primary-600 opacity-0 hover:opacity-10 transition-opacity duration-300" />
                  <div className="relative z-10">
                    <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center shadow-lg">
                      <HomeIcon className="h-10 w-10 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold mb-3">For You</h3>
                    <p className="text-sm text-gray-400 leading-relaxed mb-4">
                      Track your LOCU points, view your dashboard, and manage your account all in one place
                    </p>
                    <ul className="text-xs text-gray-500 space-y-2 text-left">
                      <li className="flex items-start">
                        <CheckCircleIcon className="h-4 w-4 text-primary-400 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Real-time points balance</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircleIcon className="h-4 w-4 text-primary-400 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Transaction history</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircleIcon className="h-4 w-4 text-primary-400 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Restaurant discounts</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </AnimatedSection>

              {/* Pay Rent */}
              <AnimatedSection delay={100}>
                <div className="glass-card glow-card rounded-2xl p-8 hover:scale-105 transition-all duration-300 relative overflow-hidden border border-white/10 text-center">
                  <div className="absolute inset-0 bg-gradient-to-br from-green-500 to-emerald-500 opacity-0 hover:opacity-10 transition-opacity duration-300" />
                  <div className="relative z-10">
                    <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center shadow-lg">
                      <BanknotesIcon className="h-10 w-10 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold mb-3">Pay Rent</h3>
                    <p className="text-sm text-gray-400 leading-relaxed mb-4">
                      Upload your rent payment receipt and earn 2% LOCU points automatically every month
                    </p>
                    <ul className="text-xs text-gray-500 space-y-2 text-left">
                      <li className="flex items-start">
                        <CheckCircleIcon className="h-4 w-4 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                        <span>2% points on every payment</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircleIcon className="h-4 w-4 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Automatic verification</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircleIcon className="h-4 w-4 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Track payment history</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </AnimatedSection>

              {/* Neighborhood */}
              <AnimatedSection delay={200}>
                <div className="glass-card glow-card rounded-2xl p-8 hover:scale-105 transition-all duration-300 relative overflow-hidden border border-white/10 text-center">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-cyan-500 opacity-0 hover:opacity-10 transition-opacity duration-300" />
                  <div className="relative z-10">
                    <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg">
                      <BuildingStorefrontIcon className="h-10 w-10 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold mb-3">Neighborhood</h3>
                    <p className="text-sm text-gray-400 leading-relaxed mb-4">
                      Discover restaurants near you, unlock exclusive discounts, and use your points for savings
                    </p>
                    <ul className="text-xs text-gray-500 space-y-2 text-left">
                      <li className="flex items-start">
                        <CheckCircleIcon className="h-4 w-4 text-blue-400 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Restaurants sorted by distance</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircleIcon className="h-4 w-4 text-blue-400 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Up to 10% discounts</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircleIcon className="h-4 w-4 text-blue-400 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Redeem points instantly</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </AnimatedSection>

              {/* Connect */}
              <AnimatedSection delay={300}>
                <div className="glass-card glow-card rounded-2xl p-8 hover:scale-105 transition-all duration-300 relative overflow-hidden border border-white/10 text-center">
                  <div className="absolute inset-0 bg-gradient-to-br from-pink-500 to-rose-500 opacity-0 hover:opacity-10 transition-opacity duration-300" />
                  <div className="relative z-10">
                    <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center shadow-lg">
                      <UserGroupIcon className="h-10 w-10 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold mb-3">Connect</h3>
                    <p className="text-sm text-gray-400 leading-relaxed mb-4">
                      Meet interesting people in your neighborhood over shared meals and build real connections
                    </p>
                    <ul className="text-xs text-gray-500 space-y-2 text-left">
                      <li className="flex items-start">
                        <CheckCircleIcon className="h-4 w-4 text-pink-400 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Verified local residents</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircleIcon className="h-4 w-4 text-pink-400 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Safe group dining</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircleIcon className="h-4 w-4 text-pink-400 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Build your community</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </AnimatedSection>
            </div>

            {/* Sign Up / Sign In CTA */}
            <AnimatedSection delay={400}>
              <div className="text-center mt-12">
                <div className="flex flex-col sm:flex-row gap-4 justify-center relative z-50">
                  <Link
                    to="/login"
                    className="group relative inline-flex items-center justify-center px-10 py-4 bg-gradient-to-r from-orange-500 via-pink-500 to-purple-600 rounded-2xl font-bold text-lg overflow-hidden transition-all duration-500 hover:scale-105 hover:shadow-2xl hover:shadow-orange-500/25 pointer-events-auto"
                  >
                    <span className="relative z-10 flex items-center gap-2">
                      Sign Up / Sign In
                      <ArrowRightIcon className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </Link>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </section>
      )}

      {/* How It Works */}
      <section className="relative py-32 px-4">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection>
            <h2 className="text-4xl md:text-6xl font-bold text-center mb-6">
              How it <span className="gradient-text">works</span>
            </h2>
            <p className="text-xl text-gray-400 text-center mb-20 max-w-2xl mx-auto">
              Three simple steps to start earning rewards on your rent.
            </p>
          </AnimatedSection>

          <div className="grid md:grid-cols-3 gap-8 relative">
            {/* Connection Line */}
            <div className="hidden md:block absolute top-1/2 left-1/4 right-1/4 h-0.5 bg-gradient-to-r from-orange-500 via-pink-500 to-purple-500 -translate-y-1/2" />
            
            {[
              { num: '01', title: 'Verify Rent', desc: 'Upload your rent agreement and verify your tenancy in minutes', color: 'orange' },
              { num: '02', title: 'Pay & Earn', desc: 'Pay your rent via UPI and automatically earn LOCU points', color: 'primary' },
              { num: '03', title: 'Redeem', desc: 'Use your points for discounts at local restaurants', color: 'primary' },
            ].map((step, idx) => (
              <AnimatedSection key={idx} delay={idx * 200}>
                <div className="relative glass-card rounded-3xl p-8 text-center hover:scale-105 transition-all duration-300 group">
                  <div className={`w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-${step.color}-500/20 to-${step.color}-600/10 flex items-center justify-center relative`}>
                    <span className={`text-3xl font-bold text-${step.color}-400`}>{step.num}</span>
                    <div className={`absolute inset-0 rounded-full bg-${step.color}-500/20 animate-ping opacity-0 group-hover:opacity-100`} />
                  </div>
                  <h3 className="text-2xl font-bold mb-3">{step.title}</h3>
                  <p className="text-gray-400">{step.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof / Testimonials */}
      {!isAuthenticated && (
        <section className="relative py-24 px-4">
          <div className="max-w-6xl mx-auto">
            <AnimatedSection>
              <div className="text-center mb-16">
                <span className="inline-block px-4 py-2 rounded-full glass-card text-sm font-medium text-primary-300 mb-4">
                  ⭐ Loved by Renters
                </span>
                <h2 className="text-4xl md:text-5xl font-bold mb-4">
                  Join <span className="gradient-text">Smart</span> Renters
                </h2>
                <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                  See what our community is saying about LOCU
                </p>
              </div>
            </AnimatedSection>


            {/* Value Props */}
            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  title: 'Earn on Every Payment',
                  desc: 'Get 2% back in LOCU points every time you pay rent. Points never expire.',
                  icon: BanknotesIcon,
                  color: 'from-green-500 to-emerald-500'
                },
                {
                  title: 'Discover Local Spots',
                  desc: 'Use your points for discounts at restaurants in your neighborhood.',
                  icon: BuildingStorefrontIcon,
                  color: 'from-blue-500 to-cyan-500'
                },
                {
                  title: 'Meet Your Neighbors',
                  desc: 'Connect with verified local residents over shared meals and events.',
                  icon: UserGroupIcon,
                  color: 'from-pink-500 to-rose-500'
                }
              ].map((item, idx) => (
                <AnimatedSection key={idx} delay={idx * 150}>
                  <div className="glass-card rounded-2xl p-6 h-full text-center">
                    <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br ${item.color} flex items-center justify-center`}>
                      <item.icon className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                    <p className="text-gray-400">{item.desc}</p>
                  </div>
                </AnimatedSection>
              ))}
            </div>

            {/* Trust Badges */}
            <AnimatedSection delay={500}>
              <div className="mt-16 text-center">
                <p className="text-gray-500 text-sm mb-4">Trusted by renters across Bangalore</p>
                <div className="flex flex-wrap justify-center items-center gap-8 opacity-50">
                  {['Bank-grade Security', 'ISO 27001 Certified', 'RBI Compliant', '256-bit Encryption'].map((badge, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-gray-400 text-sm">
                      <CheckCircleIcon className="h-4 w-4 text-green-400" />
                      {badge}
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedSection>
          </div>
        </section>
      )}

      {/* Final CTA */}
      {!isAuthenticated && (
        <section className="relative py-32 px-4">
          <AnimatedSection className="relative z-30">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-4xl md:text-6xl font-bold mb-6">
                Ready to start <span className="gradient-text">earning?</span>
              </h2>
              <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto">
                Start earning rewards on your rent today. It only takes a few minutes.
              </p>
              <Link
                to="/login"
                className="group inline-flex items-center justify-center px-12 py-6 bg-gradient-to-r from-orange-500 via-pink-500 to-purple-600 rounded-2xl font-bold text-xl hover:scale-105 transition-all duration-300 hover:shadow-2xl hover:shadow-orange-500/25"
              >
                <span className="flex items-center gap-3">
                  Get Started — It's Free
                  <ArrowRightIcon className="h-6 w-6 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            </div>
          </AnimatedSection>
        </section>
      )}
    </div>
  );
};

export default Home;
